import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PREDICTION_FIELDS } from "@/lib/constants";
import { Header } from "@/components/Header";
import { useScopedPath } from "@/lib/scoped";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

function pointsFor(pred, actual) {
  if (pred == null || actual == null) return 0;
  const diff = Math.abs(parseInt(pred, 10) - parseInt(actual, 10));
  if (diff === 0) return 3;
  if (diff === 1) return 1;
  return 0;
}

function getPointsPillClass(value) {
  if (value === 3) return "bg-[#0038FF] text-white";
  if (value === 1) return "bg-zinc-950 text-white";
  return "text-zinc-400";
}

export default function MatchDetailPage() {
  const scope = useScopedPath();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/matches/${id}/results`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const sortedPredictions = useMemo(() => {
    if (!data?.predictions) return [];
    return data.predictions
      .slice()
      .sort((a, b) => (b.points_total || 0) - (a.points_total || 0));
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-6xl mx-auto px-4 py-12 text-sm uppercase tracking-[0.2em] text-zinc-500">Loading…</div>
      </div>
    );
  }
  if (!data) return null;

  const { match, predictions, current_user_id } = data;
  const actual = match.actual;
  const myPred = predictions.find((p) => p.user_id === current_user_id);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24">
        <Link
          to={scope("/")}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-500 hover:text-zinc-950 mb-6"
          data-testid="back-to-matches"
        >
          <ArrowLeft className="h-4 w-4" /> Back to matches
        </Link>

        <div className="mb-2 flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{match.kickoff}</span>
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">·</span>
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{match.status === "finished" ? "Full Time" : "Scheduled"}</span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-zinc-950">
          {match.home_team} <span className="text-zinc-300">vs</span> {match.away_team}
        </h1>

        {!actual ? (
          <div className="mt-10 border border-dashed border-zinc-300 rounded-sm p-12 text-center" data-testid="awaiting-result">
            <p className="font-heading text-2xl font-semibold text-zinc-950">Awaiting full-time result.</p>
            <p className="mt-2 text-sm text-zinc-500">Your prediction will be scored once the admin enters the actual result.</p>
          </div>
        ) : (
          <>
            {/* Your scorecard */}
            <section className="mt-10 border border-zinc-200 rounded-sm" data-testid="my-scorecard">
              <header className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
                <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-950">Your scorecard</h2>
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
                  Total {myPred?.points_total ?? 0} pts
                </span>
              </header>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50">
                      <th className="text-left px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Stat</th>
                      <th className="text-right px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Your prediction</th>
                      <th className="text-right px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Actual result</th>
                      <th className="text-right px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PREDICTION_FIELDS.map((f) => {
                      const p = myPred?.[f.key];
                      const a = actual?.[f.key];
                      const pts = myPred ? pointsFor(p, a) : 0;
                      return (
                        <tr key={f.key} className="border-b border-zinc-100 last:border-b-0">
                          <td className="px-6 py-3 font-medium text-zinc-950">{f.label}</td>
                          <td className="px-6 py-3 text-right text-zinc-700 tabular-nums">{p ?? "—"}</td>
                          <td className="px-6 py-3 text-right text-zinc-950 font-semibold tabular-nums">{a ?? "—"}</td>
                          <td className="px-6 py-3 text-right tabular-nums">
                            <span className={`inline-flex items-center justify-end min-w-[2.5rem] px-2 py-1 rounded-sm font-bold text-xs ${getPointsPillClass(pts)}`}>
                              {pts}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Everyone's scores */}
            <section className="mt-10" data-testid="all-predictions">
              <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-950 mb-4">League scores for this match</h2>
              <div className="border border-zinc-200 rounded-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50">
                      <th className="text-left px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Player</th>
                      {PREDICTION_FIELDS.map((f) => (
                        <th key={f.key} className="text-right px-3 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{f.label}</th>
                      ))}
                      <th className="text-right px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPredictions.map((p) => (
                      <tr key={p.user_id} className={`border-b border-zinc-100 last:border-b-0 ${p.user_id === current_user_id ? "bg-[#0038FF]/5" : ""}`}>
                        <td className="px-6 py-3 font-medium text-zinc-950">
                          {p.user_name || p.user_email}{p.user_id === current_user_id ? " (you)" : ""}
                        </td>
                        {PREDICTION_FIELDS.map((f) => (
                          <td key={f.key} className="text-right px-3 py-3 text-zinc-700 tabular-nums">{p[f.key]}</td>
                        ))}
                        <td className="text-right px-6 py-3 font-bold tabular-nums text-zinc-950">{p.points_total ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
